import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { breakpoints } from 'styles';
import { getUserAccessOptions } from 'lib/courses';
import { Button, CopyButtonText, ModalButton } from 'components/common';
import { Box } from 'components/common/box';
import {
  Checkbox,
  Form,
  FormGroup,
  Select,
  Text
} from 'components/common/form';
import { CourseShare } from 'components/courses/common';

import type { FormApi } from 'informed';
import type { UserAccess } from 'shared-types';

const ShareSettings = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig: {
      adminUrl,
      authStrategies,
      isDemoEnvironment,
      limitCourseUserAccesOptions
    }
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const [loading, setLoading] = useState(false);
  const [pristine, setPristine] = useState(true);

  const userAccessOptions = getUserAccessOptions({
    includeSingleSignOn: Boolean(authStrategies?.length),
    isDemoEnvironment,
    messages
  }).filter(
    (option) =>
      !limitCourseUserAccesOptions?.length ||
      limitCourseUserAccesOptions.includes(option.value as UserAccess)
  );

  const initialValues = {
    adminAccess: course.adminAccess,
    passcodeRequired: course.passcodeRequired,
    passcodeValue: course.passcodeValue,
    userAccess: course.userAccess
  };

  const handleChange = () => setPristine(false);

  const handleUserAccessChange = (value) => {
    setPristine(false);

    if (value === 'registered' || value === 'registered-and-guests') {
      const adminAccess = formApi.current.getValue('adminAccess');
      if (adminAccess === 'private')
        formApi.current.setValue('adminAccess', 'public');
    }
  };

  const submit = async (values) => {
    setLoading(true);

    try {
      const data = {
        userAccess: values.userAccess,
        adminAccess: values.adminAccess,
        passcodeRequired: values.passcodeRequired,
        passcodeValue: values.passcodeValue
      };

      await api.put(`/admin/courses/${course.id}`, data);
      const courses = await api.get(`/admin/courses?id=${course.id}`);

      setPristine(true);
      dispatch(actions.updatePageState({ course: courses.data.courses[0] }));
      dispatch(
        actions.addNotice({
          text: messages.settingsSavedSuccessfully,
          type: 'success'
        })
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings">
      <Box title={messages.commonAccess}>
        <Form
          allowEmptyStrings={true}
          getApi={(form) => (formApi.current = form)}
          initialValues={initialValues}
          onSubmit={submit}
        >
          {({ formState }) => (
            <div className="form-elements">
              <FormGroup
                htmlFor="user-access"
                label={messages.settingsUserAccess}
                style={{ width: 260, maxWidth: '100%' }}
              >
                <Select
                  field="userAccess"
                  id="user-access"
                  menuWidth={350}
                  onChange={handleUserAccessChange}
                  options={userAccessOptions}
                />

                {formState.values.userAccess === 'guests' ? (
                  <CopyButtonText
                    successText={messages.commonCopied}
                    text={messages.commonCopyLink}
                    value={`${adminUrl}/courses/${course.slug}`}
                  />
                ) : null}
              </FormGroup>

              <FormGroup
                htmlFor="admin-access"
                label={messages.settingsAdminAccess}
                labelTooltip={messages.settingsAdminAccessInfo}
                style={{ width: 260, maxWidth: '100%', margin: 0 }}
              >
                <Select
                  field="adminAccess"
                  id="admin-access"
                  menuWidth={350}
                  onChange={handleChange}
                  options={[
                    {
                      helpText: messages.settingsAdminAccessPrivateInfo,
                      value: 'private',
                      label: messages.settingsAdminAccessPrivate,
                      isDisabled:
                        formState.values.userAccess === 'registered' ||
                        formState.values.userAccess === 'registered-and-guests'
                    },
                    {
                      helpText: messages.settingsAdminAccessPublicInfo,
                      value: 'public',
                      label: messages.settingsAdminAccessPublic,
                      isDisabled: isDemoEnvironment
                    },
                    {
                      helpText: messages.settingsAdminAccessCopyableInfo,
                      value: 'copyable',
                      label: messages.settingsAdminAccessCopyable,
                      isDisabled: isDemoEnvironment
                    }
                  ]}
                />
              </FormGroup>

              <FormGroup
                label={messages.settingsUserAccessPasscode}
                style={{ width: '100%', maxWidth: '100%', margin: 0 }}
              >
                <div className="passcode">
                  <Checkbox
                    field="passcodeRequired"
                    id="passcode-required"
                    label={messages.settingsUserAccessRequirePasscode}
                    onChange={handleChange}
                    style={{ display: 'inline-block', marginRight: 10 }}
                  />

                  <Text
                    disabled={!formState.values.passcodeRequired}
                    field="passcodeValue"
                    id="passcode-value"
                    onChange={handleChange}
                    placeholder={messages.settingsUserAccessPasscode}
                    style={{ width: 200, maxWidth: '100%' }}
                  />
                </div>
              </FormGroup>

              <div className="actions">
                <Button
                  category="success"
                  disabled={pristine}
                  loading={loading}
                  text={messages.commonSaveChanges}
                  type="submit"
                />

                <div className="share-links">
                  <ModalButton
                    category="light"
                    icon="share"
                    modalProps={{
                      closeButton: true,
                      title: messages.commonSharing
                    }}
                    text={messages.commonSharing}
                  >
                    <CourseShare course={course} showDownloadButton={true} />
                  </ModalButton>
                </div>
              </div>

              <style jsx>{`
                .form-elements {
                  display: flex;
                  flex-direction: column;
                  flex-wrap: wrap;
                  gap: 15px;
                }
                .passcode {
                  align-items: center;
                  display: flex;
                  flex-wrap: wrap;
                  gap: 10px;
                }
                .actions {
                  display: flex;
                  justify-content: space-between;
                  margin-top: 20px;
                  width: 100%;
                }
                .share-links {
                  display: none;
                }
                @media (max-width: ${breakpoints.large}px) {
                  .form-elements {
                    flex-direction: row;
                    gap: 15px 40px;
                  }
                  .share-links {
                    display: block;
                  }
                }
              `}</style>
            </div>
          )}
        </Form>
      </Box>
    </div>
  );
};

export default ShareSettings;
